import { ChevronDown, History, RefreshCw } from "lucide-react";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { TabModulo } from "@/components/TabModulo";
import { SEZIONI_M3 } from "@/lib/nav-config";
import { Skeleton } from "@/components/ui/skeleton";
import { usePartenze } from "@/hooks/useM3";
import { cn } from "@/lib/utils";
import type { PartenzaRiga, UscitaStorica } from "@/types/api";

/**
 * Il tabellone delle partenze — sostituisce il foglio che Marina stampava ogni
 * giorno per il presidente.
 *
 * Pensata per restare aperta: si aggiorna da sola ogni minuto e i caratteri sono
 * grandi perché si legga da lontano. Quando si deciderà di metterla su una TV,
 * l'unica cosa che serve è una rotta pubblica con token: i dati arrivano già da
 * un endpoint che non dipende da chi guarda.
 */
export function PartenzePage() {
  const [sp, setSp] = useSearchParams();
  const giorno = sp.get("giorno") ?? undefined;
  const { data, isLoading, isFetching, refetch } = usePartenze(giorno);

  const oggi = new Date().toISOString().slice(0, 10);
  const quando = data?.giorno ?? giorno ?? oggi;
  const eOggi = quando === oggi;

  return (
    <div className="mx-auto max-w-6xl px-4 pb-8 pt-5">
      {/* Come il Calendario: sezione di Spedizioni con una rotta propria, quindi
          la stessa fila di tab. Il titolo (che qui era `text-3xl`, contro il
          `2xl` del Calendario e il `15px` del modulo) sta ora nell'header. */}
      <TabModulo sezioni={SEZIONI_M3} modulo="M3" />

      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm text-muted-foreground">
            {eOggi ? "Oggi" : "Il giorno"}{" "}
            <b>
              {new Date(quando).toLocaleDateString("it-IT", {
                weekday: "long",
                day: "numeric",
                month: "long",
              })}
            </b>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={quando}
            onChange={(e) => {
              const n = new URLSearchParams(sp);
              if (e.target.value === oggi) n.delete("giorno");
              else n.set("giorno", e.target.value);
              setSp(n, { replace: true });
            }}
            className="h-9 rounded-md border bg-card px-2 text-sm"
          />
          <button
            type="button"
            onClick={() => refetch()}
            className="inline-flex h-9 items-center gap-1.5 rounded-md border px-3 text-sm hover:bg-muted"
          >
            <RefreshCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
            Aggiorna
          </button>
        </div>
      </header>

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : !data || data.totale === 0 ? (
        <div className="rounded-xl border border-dashed p-12 text-center">
          <p className="text-lg font-medium text-muted-foreground">
            Nessuna partenza segnata per questo giorno.
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            Le partenze compaiono qui quando la logistica le segna sul carico.
          </p>
        </div>
      ) : (
        <>
          <div className="mb-6 flex gap-8">
            <Numero valore={data.da_far_uscire} label="Da far uscire" evidenza />
            <Numero valore={data.totale - data.da_far_uscire} label="Già uscite" />
            <Numero
              valore={data.partenze.reduce((s, p) => s + (p.pallet ?? 0), 0)}
              label="Pallet totali"
            />
          </div>

          <div className="overflow-hidden rounded-xl border">
            {data.partenze.map((p, i) => (
              <RigaPartenza key={p.id} p={p} alternata={i % 2 === 1} />
            ))}
          </div>
        </>
      )}

      {/* Lo storico: il tabellone deve rispondere anche a «cosa è partito».
          Senza, la merce uscita si trovava solo nel filtro «Chiusi» del
          tabellone dei carichi previsti, che non è dove uno va a cercarla. */}
      <Storico righe={data?.storico ?? []} />
    </div>
  );
}

function Storico({ righe }: { righe: UscitaStorica[] }) {
  const [aperto, setAperto] = useState(false);
  if (righe.length === 0) return null;

  return (
    <section className="mt-10">
      <button
        type="button"
        onClick={() => setAperto((v) => !v)}
        className="flex w-full items-center gap-2 border-b pb-2 text-left"
      >
        <History className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
          Già usciti ({righe.length})
        </h2>
        <ChevronDown
          className={cn(
            "ml-auto h-4 w-4 text-muted-foreground transition-transform",
            aperto && "rotate-180",
          )}
        />
      </button>

      {aperto && (
        <ul className="divide-y">
          {righe.map((r) => (
            <li key={r.id} className="flex flex-wrap items-baseline gap-x-4 gap-y-1 py-2.5">
              <span className="w-24 shrink-0 text-xs tabular-nums text-muted-foreground">
                {r.uscito_il ? new Date(r.uscito_il).toLocaleDateString("it-IT") : "—"}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm font-medium">{r.cliente}</span>
              <span className="text-xs text-muted-foreground">
                {r.commesse.length > 0 ? `commessa ${r.commesse.join(", ")}` : "—"}
                {r.ndp && ` · NDP ${r.ndp}`}
                {r.ddt && ` · DDT ${r.ddt}`}
              </span>
              {/* Senza documento non c'è niente da scrivere accanto alla
                  commessa, e la riga sembrerebbe incompleta invece che
                  dichiarata: il badge dice che è una scelta, non una mancanza. */}
              {r.uscita_forzata_motivo && (
                <span
                  className="rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[9.5px] font-semibold text-amber-900"
                  title={
                    (r.uscita_forzata_motivo === "evasa_in_cube"
                      ? "NDP evasa in Cube senza DDT"
                      : r.uscita_forzata_motivo === "ndp_non_caricata"
                        ? "NDP emessa ma non caricata nel portale"
                        : "Spedizione senza NDP (carico a mano)") +
                    (r.uscita_forzata_nota ? ` · ${r.uscita_forzata_nota}` : "")
                  }
                >
                  {r.uscita_forzata_motivo === "evasa_in_cube" ? "senza DDT" : "senza NDP"}
                </span>
              )}
              <span className="w-16 text-right text-sm tabular-nums">
                {r.pallet != null ? `${Math.round(r.pallet)} plt` : "—"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Numero({
  valore,
  label,
  evidenza,
}: {
  valore: number;
  label: string;
  evidenza?: boolean;
}) {
  return (
    <div>
      <div
        className={cn(
          "text-4xl font-bold tabular-nums tracking-tight",
          evidenza ? "text-brand-600" : "text-muted-foreground",
        )}
      >
        {Math.round(valore)}
      </div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}

function RigaPartenza({ p, alternata }: { p: PartenzaRiga; alternata: boolean }) {
  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-6 gap-y-2 border-b px-5 py-4 last:border-b-0",
        alternata && "bg-muted/30",
        p.uscito && "opacity-60",
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="truncate text-xl font-semibold tracking-tight">{p.cliente}</div>
        <div className="mt-0.5 text-xs text-muted-foreground">
          {p.commesse.length > 0
            ? `commessa ${p.commesse.join(", ")}`
            : p.riferimenti.join(", ") || "—"}
          {p.ndp && ` · NDP ${p.ndp}`}
          {p.ddt && ` · DDT ${p.ddt}`}
          {/* La NDP chiusa in Cube senza DDT (06-10-2026): senza il segno sembrerebbe
              un DDT mancante, non un'uscita che il gestionale ha chiuso così. */}
          {p.uscita_forzata_motivo === "evasa_in_cube" && " · chiusa in Cube senza DDT"}
        </div>
      </div>

      <div className="text-right">
        <div className="text-2xl font-bold tabular-nums">
          {p.pallet != null ? Math.round(p.pallet) : "—"}
        </div>
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">pallet</div>
      </div>

      <div className="w-32 text-right">
        {p.uscito ? (
          <span className="rounded-full border border-emerald-200 bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-800">
            Uscita
          </span>
        ) : (
          <span className="rounded-full border border-amber-200 bg-amber-100 px-3 py-1 text-sm font-medium text-amber-900">
            In partenza
          </span>
        )}
      </div>
    </div>
  );
}
